<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">{{ saludo }}</h2>
        <p class="db-page-subtitle">Este es el estado de la web a {{ hoy }}.</p>
      </div>
    </header>

    <!-- Estadísticas -->
    <section class="res-stats" aria-label="Estadísticas">
      <button
        v-for="s in stats"
        :key="s.key"
        type="button"
        class="res-stat db-card"
        @click="$emit('navigate', s.destino)"
      >
        <span class="res-stat-icon" :class="'is-' + s.tono">
          <span class="material-symbols-outlined">{{ s.icon }}</span>
        </span>
        <span class="res-stat-body">
          <span class="res-stat-label">{{ s.label }}</span>
          <span v-if="loading" class="db-skeleton res-stat-skeleton"></span>
          <span v-else class="res-stat-value">{{ s.valor }}</span>
          <span v-if="!loading && s.detalle" class="res-stat-detail">{{ s.detalle }}</span>
        </span>
      </button>
    </section>

    <div class="res-columns">
      <!-- Últimas noticias -->
      <section class="db-card">
        <div class="db-card-head">
          <h3 class="db-card-title">
            <span class="material-symbols-outlined">history</span> Últimas noticias
          </h3>
          <button
            v-if="puede(P.NOTICIAS) || puede(P.CARITAS)"
            type="button"
            class="db-btn db-btn--ghost db-btn--sm"
            @click="$emit('navigate', puede(P.NOTICIAS) ? 'noticias' : 'noticiasCaritas')"
          >
            Ver todas
          </button>
        </div>

        <div v-if="loading" class="db-card-body res-skeletons">
          <div v-for="n in 4" :key="n" class="db-skeleton" style="height: 48px"></div>
        </div>
        <div v-else-if="!recientes.length" class="db-empty">
          <span class="material-symbols-outlined">article</span>
          <p>Todavía no hay noticias.</p>
        </div>
        <ul v-else class="res-list">
          <li v-for="n in recientes" :key="n.tipo + n.id" class="res-list-item">
            <span class="db-thumb">
              <img v-img-fallback v-if="n.imagenPortada" :src="`${base}/img/minGallery/${n.imagenPortada}`" alt="" loading="lazy" />
              <span v-else class="material-symbols-outlined">image</span>
            </span>
            <span class="res-list-body">
              <span class="res-list-title">{{ n.titulo }}</span>
              <span class="res-list-meta">
                {{ formatFecha(n.fechaPublicacion) }}
                <span v-if="n.tipo === 'caritas'" class="db-badge db-badge--gold">Cáritas</span>
              </span>
            </span>
            <span class="db-badge" :class="n.estadoNoticia ? 'db-badge--success' : ''">
              {{ n.estadoNoticia ? "Publicada" : "Borrador" }}
            </span>
          </li>
        </ul>
      </section>

      <!-- Accesos rápidos -->
      <section class="db-card">
        <div class="db-card-head">
          <h3 class="db-card-title">
            <span class="material-symbols-outlined">bolt</span> Accesos rápidos
          </h3>
        </div>
        <div class="db-card-body res-quick">
          <button
            v-for="a in accesos"
            :key="a.destino"
            type="button"
            class="res-quick-item"
            @click="$emit('navigate', a.destino)"
          >
            <span class="material-symbols-outlined">{{ a.icon }}</span>
            <span>{{ a.texto }}</span>
          </button>
          <router-link :to="{ name: 'inicio' }" target="_blank" class="res-quick-item">
            <span class="material-symbols-outlined">public</span>
            <span>Ver la web</span>
          </router-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "@/directives/axios.js";
import { formatFecha, parseFecha, toISODate, PERMISOS } from "@/directives/dbDialogs.js";

export default {
  name: "ResumenDashboard",
  data() {
    return {
      P: PERMISOS,
      loading: true,
      noticias: [],
      noticiasCaritas: [],
      galerias: [],
      popups: [],
      enlaces: [],
      base: this.$media,
    };
  },
  computed: {
    saludo() {
      const h = new Date().getHours();
      return h < 13 ? "Buenos días" : h < 21 ? "Buenas tardes" : "Buenas noches";
    },
    hoy() {
      return new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
    },
    imagenesTotales() {
      return this.$store.state.allImagenes.length;
    },
    popupsActivos() {
      const hoy = toISODate(new Date());
      return this.popups.filter((p) => (p.fechas || "").split(",").some((f) => f.trim() >= hoy)).length;
    },
    stats() {
      const publicadas = (lista) => lista.filter((n) => Number(n.estadoNoticia) === 1 || n.estadoNoticia === true).length;
      return [
        {
          key: "noticias",
          permiso: PERMISOS.NOTICIAS,
          label: "Noticias",
          icon: "newspaper",
          tono: "primary",
          valor: this.noticias.length,
          detalle: `${publicadas(this.noticias)} publicadas`,
          destino: "noticias",
        },
        {
          key: "caritas",
          permiso: PERMISOS.CARITAS,
          label: "Noticias Cáritas",
          icon: "volunteer_activism",
          tono: "gold",
          valor: this.noticiasCaritas.length,
          detalle: `${publicadas(this.noticiasCaritas)} publicadas`,
          destino: "noticiasCaritas",
        },
        {
          key: "galerias",
          permiso: PERMISOS.GALERIAS,
          label: "Galerías",
          icon: "photo_library",
          tono: "success",
          valor: this.galerias.length,
          detalle: this.imagenesTotales ? `${this.imagenesTotales} imágenes en total` : "",
          destino: "galeria",
        },
        {
          key: "popups",
          permiso: PERMISOS.POPUPS,
          label: "Avisos emergentes",
          icon: "campaign",
          tono: "warning",
          valor: this.popups.length,
          detalle: `${this.popupsActivos} programados`,
          destino: "popUp",
        },
        {
          key: "enlaces",
          permiso: PERMISOS.ENLACES,
          label: "Sitios de interés",
          icon: "link",
          tono: "primary",
          valor: this.enlaces.length,
          detalle: `${this.enlaces.filter((e) => e.activo == 1 || e.activo === true).length} visibles`,
          destino: "urls",
        },
      ].filter((s) => this.puede(s.permiso));
    },
    recientes() {
      const todas = [
        ...this.noticias.map((n) => ({ ...n, tipo: "parroquia" })),
        ...this.noticiasCaritas.map((n) => ({ ...n, tipo: "caritas" })),
      ];
      return todas
        .sort((a, b) => (parseFecha(b.fechaPublicacion) || 0) - (parseFecha(a.fechaPublicacion) || 0))
        .slice(0, 6);
    },
    accesos() {
      return [
        { destino: "noticias", permiso: PERMISOS.NOTICIAS, icon: "post_add", texto: "Gestionar noticias" },
        { destino: "noticiasCaritas", permiso: PERMISOS.CARITAS, icon: "volunteer_activism", texto: "Noticias de Cáritas" },
        { destino: "galeria", permiso: PERMISOS.GALERIAS, icon: "add_photo_alternate", texto: "Galerías y fotos" },
        { destino: "popUp", permiso: PERMISOS.POPUPS, icon: "campaign", texto: "Programar avisos" },
        { destino: "horarios", permiso: PERMISOS.HORARIOS, icon: "schedule", texto: "Editar horarios" },
        { destino: "usuarios", permiso: PERMISOS.USUARIOS, icon: "group", texto: "Gestionar usuarios" },
      ].filter((a) => this.puede(a.permiso));
    },
  },
  methods: {
    formatFecha,
    puede(permiso) {
      return this.$store.getters.puede(permiso);
    },
  },
  async mounted() {
    // Solo se piden los datos de las secciones que el usuario puede gestionar.
    const si = (permiso, url) => (this.puede(permiso) ? axios.get(url) : Promise.resolve(null));
    const peticiones = [
      si(PERMISOS.NOTICIAS, "/noticias/todasNoticias"),
      si(PERMISOS.CARITAS, "/noticiasCaritas/todasNoticias"),
      si(PERMISOS.GALERIAS, "/imagenes/todasGalerias"),
      si(PERMISOS.POPUPS, "/popup/todosPopup"),
      si(PERMISOS.ENLACES, "/enlaces/todosEnlaces"),
    ];
    const [noticias, caritas, galerias, popups, enlaces] = await Promise.allSettled(peticiones);
    const datos = (r, campo) => (r.status === "fulfilled" && r.value && r.value.data[campo]) || [];
    this.noticias = datos(noticias, "todasNoticias");
    this.noticiasCaritas = datos(caritas, "todasNoticias");
    this.galerias = datos(galerias, "todasGalerias");
    this.popups = datos(popups, "todosPopUp");
    this.enlaces = datos(enlaces, "todosEnlaces");
    this.loading = false;
  },
};
</script>

<style scoped>
.res-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.res-stat {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
}

.res-stat:hover {
  transform: translateY(-2px);
  border-color: var(--sd-primary);
  box-shadow: var(--sd-shadow-lg);
}

.res-stat-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
}

.res-stat-icon .material-symbols-outlined {
  font-size: 24px;
}

.res-stat-icon.is-primary {
  background: var(--sd-primary-soft);
  color: var(--sd-primary);
}

.res-stat-icon.is-gold {
  background: var(--sd-gold-soft);
  color: var(--sd-gold);
}

.res-stat-icon.is-success {
  background: var(--sd-success-soft);
  color: var(--sd-success);
}

.res-stat-icon.is-warning {
  background: var(--sd-warning-soft);
  color: var(--sd-warning);
}

.res-stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.res-stat-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--sd-muted);
}

.res-stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--sd-text);
}

.res-stat-skeleton {
  width: 60px;
  height: 32px;
  margin: 4px 0;
}

.res-stat-detail {
  font-size: 0.78rem;
  color: var(--sd-muted);
}

.res-columns {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  gap: 24px;
  align-items: start;
}

@media (max-width: 1000px) {
  .res-columns {
    grid-template-columns: 1fr;
  }
}

.res-skeletons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.res-list {
  margin: 0;
  padding: 6px 0;
  list-style: none;
}

.res-list-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 20px;
}

.res-list-item + .res-list-item {
  border-top: 1px solid var(--sd-border);
}

.res-list-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.res-list-title {
  overflow: hidden;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--sd-text);
}

.res-list-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--sd-muted);
}

.res-quick {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.res-quick-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 14px;
  border: 1px solid var(--sd-border);
  border-radius: 12px;
  background: var(--sd-surface-2);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sd-text);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.res-quick-item:hover {
  border-color: var(--sd-primary);
  color: var(--sd-primary);
  text-decoration: none;
}

.res-quick-item .material-symbols-outlined {
  font-size: 22px;
  color: var(--sd-primary);
}
</style>
